import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, Loader2, MessageSquare } from "lucide-react";
import {
  AddApplicationMessage,
  ReadLastNMessagesForApplication,
  ChatMessage,
  markThreadAsRead,
  getProfilePictureUrl,
  getChatThread,
  fetchApplicationData,
  fetchCompanyData,
  fetchTalentProfileByApplication
} from "@/lib/supabase";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useAuth } from "@/features/auth/context/AuthContext";

interface ChatDrawerProps {
  applicationId: string;
}

export function ChatDrawer({ applicationId }: ChatDrawerProps) {
  const { t } = useTranslation(["common", "applications"]);
  const { profile } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessageText, setNewMessageText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [partnerName, setPartnerName] = useState("");

  const currentUserId = profile?.id;

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const msgs = await ReadLastNMessagesForApplication(applicationId);
      // The API returns messages in descending order (newest first), so we reverse for display
      setMessages(msgs.reverse());

      if (msgs.length > 0) {
        const threadId = msgs[0].chat_thread;
        await markThreadAsRead(threadId);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    async function fetchPartnerInfo() {
      if (!currentUserId || !applicationId) return;

      try {
        const application = await fetchApplicationData(applicationId);
        if (!application) return;

        // Determine if current user is the talent or the company/admin
        // If current user is the talent of the application, show Company info
        if (application.talent_id === currentUserId) {
          const company = await fetchCompanyData(application.company_id);
          if (company) {
            setPartnerName(company.name);
          }
        } else {
          // Current user is Admin/Partner/Company -> Show Talent Info
          const talent = await fetchTalentProfileByApplication(
            application.talent_id
          );
          if (talent) {
            setPartnerName(`${talent.given_name} ${talent.family_name}`);
            if (talent.profile_picture) {
              const url = await getProfilePictureUrl(talent.profile_picture);
              setAvatarUrl(url);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching chat partner info:", error);
      }
    }

    if (isOpen) {
      fetchPartnerInfo();
    }
  }, [isOpen, applicationId, currentUserId]);

  useEffect(() => {
    if (isOpen && applicationId) {
      fetchMessages();
      setHasUnreadMessages(false);
    }
  }, [isOpen, applicationId, fetchMessages]);

  useEffect(() => {
    async function checkUnreadMessages() {
      if (applicationId) {
        try {
          const thread = await getChatThread(applicationId);
          if (thread) {
            const lastMessageAt = new Date(thread.last_message_at).getTime();
            const lastTimeOpened = new Date(thread.last_time_opened).getTime();
            if (lastMessageAt > lastTimeOpened) {
              setHasUnreadMessages(true);
            }
          }
        } catch (error) {
          console.error("Error checking unread messages:", error);
        }
      }
    }
    // Only check if not open
    if (!isOpen) {
      checkUnreadMessages();
    }
  }, [applicationId, isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessageText.trim() || !currentUserId) return;

    setSending(true);
    try {
      const newMessage = await AddApplicationMessage(
        applicationId,
        currentUserId,
        newMessageText
      );
      setMessages((prev) => [...prev, newMessage]);
      setNewMessageText("");
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  if (!currentUserId) return null;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-2 relative">
          <MessageSquare className="h-4 w-4" />
          {t("applications:chat_button_label")}
          {hasUnreadMessages && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full border-2 border-white" />
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col h-full p-0 gap-0">
        <SheetHeader className="p-4 border-b flex flex-row items-center gap-3 space-y-0">
          <Avatar className="h-10 w-10">
            <AvatarImage src={avatarUrl || undefined} />
            <AvatarFallback>
              {partnerName
                ? partnerName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                : "?"}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start">
            <SheetTitle className="text-base">{partnerName}</SheetTitle>
            <p className="text-xs text-muted-foreground">
              {t("applications:application_chat_subtitle")}
            </p>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              {t("applications:no_messages_yet")}
            </div>
          ) : (
            messages.map((msg) => {
              const isMe = msg.sender_id === currentUserId;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-2 text-sm ${
                      isMe
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    <p>{msg.message_text}</p>
                    <p
                      className={`text-[10px] mt-1 ${
                        isMe
                          ? "text-primary-foreground/70"
                          : "text-muted-foreground"
                      }`}
                    >
                      {format(new Date(msg.sent_at), "HH:mm")}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={scrollRef} />
        </div>

        <div className="p-4 border-t bg-background">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              placeholder={t("applications:type_message_placeholder")}
              disabled={sending}
              className="flex-1"
            />
            <Button
              type="submit"
              size="icon"
              disabled={sending || !newMessageText.trim()}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
